import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserControllerV2Service } from '@app/generated-api';
import { createLazyLoadedState } from '@app/v2/utility';
import { AccessGroupEnum } from '@models/accessGroup';
import { AuthService } from '@services/auth/auth.service';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs';
@Injectable({
    providedIn: 'root',
})
export class UserServiceV2 {
    public static isCrc = signal<boolean | undefined>(undefined);

    private apiUserService = inject(UserControllerV2Service);
    private readonly authService = inject(AuthService);

    readonly allUsers = createLazyLoadedState(() => ({
        fetch$: () => this.apiUserService.userControllerV2GetUsers().pipe(map((res) => res.users)),
    }));

    clearUsersList() {
        this.allUsers.clear();
    }

    readonly currentUserData = createLazyLoadedState(() => ({
        fetch$: () =>
            this.apiUserService.userControllerV2GetUserData().pipe(
                tap((userData) => {
                    UserServiceV2.isCrc.set(userData?.accessGroupId === AccessGroupEnum.CRC);
                })
            ),
    }));

    readonly hasAccount = createLazyLoadedState(() => ({
        fetch$: () => this.apiUserService.userControllerV2HasAccount(),
    }));

    private readonly hasAccount$ = this.authService.getIsAuthenticated().pipe(
        filter(Boolean),
        switchMap(() => this.hasAccount.get$()),
        shareReplay()
    );

    hasAllPermissions$(permissions: string[]) {
        return this.hasAccount$.pipe(
            filter(Boolean),
            switchMap(() => this.currentUserData.get$()),
            filter(Boolean),
            map((userData) => {
                if (!userData.accessGroupId || !userData.userPermissions) return false;

                const lookupSet = new Set(userData.userPermissions);
                return permissions.every((permission) => lookupSet.has(permission));
            })
        );
    }

    hasAnyPermission$(permissions: string[]) {
        return this.hasAccount$.pipe(
            filter(Boolean),
            switchMap(() => this.currentUserData.get$()),
            filter(Boolean),
            map((userData) => {
                if (!userData.accessGroupId || !userData.userPermissions) return false;

                const lookupSet = new Set(userData.userPermissions);
                return permissions.some((permission) => lookupSet.has(permission));
            })
        );
    }

    isRole$(roles: AccessGroupEnum[]) {
        return this.hasAccount$.pipe(
            filter(Boolean),
            switchMap(() => this.currentUserData.get$()),
            map((x) => roles.includes(x.accessGroupId))
        );
    }

    readonly permissions = {
        request: {
            access: {
                basic$: this.hasAnyPermission$(['requests.access.basic']),
                elevated$: this.hasAnyPermission$(['requests.access.elevated']),
            },
            statuses: {
                basicView$: this.hasAnyPermission$(['requests.statuses.view.basic']),
                extendedView$: this.hasAnyPermission$(['requests.statuses.view.extended']),
            },
            additionalData: {
                lastModifiedView$: this.hasAnyPermission$(['requests.additional-data.last-modified.view']),
            },
        },
        site: {
            access: {
                basic$: this.hasAnyPermission$(['site.access.basic']),
                elevated$: this.hasAnyPermission$(['site.access.elevated']),
            },
            create$: this.hasAnyPermission$(['site.create']),
        },
        study: {
            SET$: this.hasAnyPermission$(['study.expense-management']),
            overview: {
                page: {
                    view$: this.hasAnyPermission$(['study.overview.page.view']),
                },
                crc: {
                    page: {
                        view$: this.hasAllPermissions$(['study.overview.crc.page.view']),
                    },
                },
            },
            paymentApprovalThresholds: {
                read$: this.hasAnyPermission$(['study.payment-approval-thresholds.read']),
                edit$: this.hasAnyPermission$(['study.payment-approval-thresholds.edit']),
            },
        },
        transferToAnotherSite: this.hasAnyPermission$(['patients.transfer-to-another-site']),
        patientPace: {
            invite$: this.hasAnyPermission$(['patients.patient-portal.invite']),
            revoke$: this.hasAnyPermission$(['patients.patient-portal.revoke']),
        },
        user: {
            access: {
                allView$: this.hasAnyPermission$(['user.all.view']),
                invite$: this.hasAnyPermission$(['user.access.invite']),
                rejectAccessRequest$: this.hasAnyPermission$(['user.access.reject-access-request']),
                revoke$: this.hasAnyPermission$(['user.access.revoke']),
                grant$: this.hasAnyPermission$(['user.access.grant']),
            },
        },
        patient: {
            access: {
                basic$: this.hasAnyPermission$(['patients.access.basic']),
                elevated$: this.hasAnyPermission$(['patients.access.elevated']),
            },
            pcsIdView$: this.hasAnyPermission$(['patients.pcs-id.view']),
            editRequest: {
                listView$: this.hasAnyPermission$(['patients-edit-request.list.view']),
                notificationSubmittedView$: this.hasAnyPermission$([
                    'notification.submitted-patient-edit-request.view',
                ]),
            },
            edit$: this.hasAnyPermission$(['patients.edit']),
            create$: this.hasAnyPermission$(['patients.create']),
            delete$: this.hasAnyPermission$(['patients.delete']),
            viewPayQuickerDetails$: this.hasAnyPermission$(['patients.pq-account-details.view']),
            sendPQAccountRegistrationReminder$: this.hasAnyPermission$([
                'patients.pq-account-registration-reminder.send',
            ]),
        },
        invitationApproval: {
            list$: this.hasAnyPermission$(['invitation-approvals.list.view']),
            view$: this.hasAnyPermission$(['invitation-approvals.view']),
            approve$: this.hasAnyPermission$(['invitation-approvals.approve']),
            registerIssue$: this.hasAnyPermission$(['invitation-approvals.register-issue']),
            resolveIssue$: this.hasAnyPermission$(['invitation-approvals.resolve-issue']),
        },
        paymentApprovals: {
            pageView$: this.hasAnyPermission$(['payment-approvals.page.view']),
            list$: this.hasAnyPermission$(['payment-approvals.list.view']),
            get$: this.hasAnyPermission$(['payment-approvals.get']),
            deny$: this.hasAnyPermission$(['payment-approvals.deny']),
            payoutIssue$: this.hasAnyPermission$(['payment-approvals.payout-issue']),
            resolveIssue$: this.hasAnyPermission$(['payment-approvals.resolve-issue']),
            resolveManually$: this.hasAnyPermission$(['payment-approvals.resolve-manually']),
        },
    };

    readonly signalPermissions = {
        request: {
            access: {
                basic: toSignal(this.permissions.request.access.basic$),
                elevated: toSignal(this.permissions.request.access.elevated$),
            },
            statuses: {
                basicView: toSignal(this.permissions.request.statuses.basicView$),
                extendedView: toSignal(this.permissions.request.statuses.extendedView$),
            },
            additionalData: {
                lastModifiedView: toSignal(this.permissions.request.additionalData.lastModifiedView$),
            },
        },
        site: {
            access: {
                basic: toSignal(this.permissions.site.access.basic$),
                elevated: toSignal(this.permissions.site.access.elevated$),
            },
            create: toSignal(this.permissions.site.create$),
        },
        study: {
            SET: toSignal(this.permissions.study.SET$),
            overview: {
                page: {
                    view: toSignal(this.permissions.study.overview.page.view$),
                },
                crc: {
                    page: {
                        view: toSignal(this.permissions.study.overview.crc.page.view$),
                    },
                },
            },
            paymentApprovalThresholds: {
                read: toSignal(this.permissions.study.paymentApprovalThresholds.read$),
                edit: toSignal(this.permissions.study.paymentApprovalThresholds.edit$),
            },
        },
        transferToAnotherSite: toSignal(this.permissions.transferToAnotherSite),
        patientPace: {
            invite: toSignal(this.permissions.patientPace.invite$),
            revoke: toSignal(this.permissions.patientPace.revoke$),
        },
        user: {
            access: {
                allView: toSignal(this.permissions.user.access.allView$),
                invite: toSignal(this.permissions.user.access.invite$),
                rejectAccessRequest: toSignal(this.permissions.user.access.rejectAccessRequest$),
                revoke: toSignal(this.permissions.user.access.revoke$),
                grant: toSignal(this.permissions.user.access.grant$),
            },
        },
        patient: {
            access: {
                basic: toSignal(this.permissions.patient.access.basic$),
                elevated: toSignal(this.permissions.patient.access.elevated$),
            },
            pcsIdView: toSignal(this.permissions.patient.pcsIdView$),
            editRequest: {
                listView: toSignal(this.permissions.patient.editRequest.listView$),
                notificationSubmittedView: toSignal(this.permissions.patient.editRequest.notificationSubmittedView$),
            },
            edit: toSignal(this.permissions.patient.edit$),
            create: toSignal(this.permissions.patient.create$),
            delete: toSignal(this.permissions.patient.delete$),
            viewPayQuickerDetails: toSignal(this.permissions.patient.viewPayQuickerDetails$),
            sendPQAccountRegistrationReminder: toSignal(this.permissions.patient.sendPQAccountRegistrationReminder$),
        },
        invitationApproval: {
            list: toSignal(this.permissions.invitationApproval.list$),
            view: toSignal(this.permissions.invitationApproval.view$),
            approve: toSignal(this.permissions.invitationApproval.approve$),
            registerIssue: toSignal(this.permissions.invitationApproval.registerIssue$),
            resolveIssue: toSignal(this.permissions.invitationApproval.resolveIssue$),
        },
        paymentApprovals: {
            pageView: toSignal(this.permissions.paymentApprovals.pageView$),
            list: toSignal(this.permissions.paymentApprovals.list$),
            get: toSignal(this.permissions.paymentApprovals.get$),
            deny: toSignal(this.permissions.paymentApprovals.deny$),
            payoutIssue: toSignal(this.permissions.paymentApprovals.payoutIssue$),
            resolveIssue: toSignal(this.permissions.paymentApprovals.resolveIssue$),
            resolveManually: toSignal(this.permissions.paymentApprovals.resolveManually$),
        },
    };
}
